import { Box, Dialog, DialogActions, DialogContent, Grid2, styled, Typography } from "@mui/material";
import CloseButton from "../../../../Components/CloseButton";
import { PrimaryFormLoadingActionButton } from "../../../../Features/Table/GenericDraggableTable/TableStyles";
import { useTranslation } from "react-i18next";
import SingleDatePicker from "../../../../Helper/DatePicker/SingleDatePicker";
import SingleTimePicker from "../../../../Helper/DatePicker/SingleTimePicker";
import { useEffect, useState } from "react";
import { downloadIotStateData } from "../../../../Api/IoTApi";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import dayjs, { utc } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import Cookies from "universal-cookie";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
      borderRadius: 8,
      boxShadow: theme.shadows[5],
      width: "455px",
      maxWidth: "90%",
    },
  }));

export default function DeviceHealthDownloadDataDialog({open, handleClose}) {

    const { batteryID } = useBatteryInfo()

    const {t} = useTranslation(['common', 'generic_table'])
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [showEmptyDateTimeError, setShowEmptyDateTimeError] = useState(false);
    const [showHourDifferenceError, setShowHourDifferenceError] = useState(false);
    const [showInvalidTimeError, setShowInvalidTimeError] = useState(false);

    const cookies = new Cookies();
    const timezone = cookies.get("timeZone") || "UTC";
    const localDate = dayjs().tz(timezone).format("YYYY-MM-DD");

    const onDateChange = (value) => {
        setSelectedDate(value);
    }

    const onStartTimeChange = (value) => {
        setSelectedStartTime(value);

    }
    const onEndTimeChange = (value) => {
        setSelectedEndTime(value);
    }

    const downloadCSVFile = (response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        // Try to extract filename from Content-Disposition
        const disposition = response.headers.get("Content-Disposition");
        let fileName = "data.csv";
        if (disposition && disposition.includes("filename=")) {
            fileName = disposition.split("filename=")[1].replace(/"/g, "").trim();
        }

        // Create a hidden download link and click it
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Free up memory
        window.URL.revokeObjectURL(url);
    }

    const hideAllErrors = () => {
        setShowHourDifferenceError(false);
        setShowEmptyDateTimeError(false);
        setShowInvalidTimeError(false);
    }

    const checkDatesValidation = (checkForEmpty = true) => {
        hideAllErrors();

        if (checkForEmpty) {
            if (!selectedStartTime || !selectedEndTime || !selectedDate) {
                setShowEmptyDateTimeError(true);
                return false;
            }
        } else {
            if (!selectedStartTime || !selectedEndTime) {
                return false;
            }
        }

        setShowEmptyDateTimeError(false);

        const diffInMinutes = selectedEndTime.diff(selectedStartTime, "minute");

        if (diffInMinutes <= 0) {
            setShowInvalidTimeError(true);
            return false;
        }
        setShowInvalidTimeError(false);

        if (diffInMinutes > 60) {
            setShowHourDifferenceError(true);
            return false;
        }
        setShowHourDifferenceError(false);
        return true;
    }

    const prepareDatesForApi = () => {
        const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'

        const startDateTime = selectedDate
            .hour(selectedStartTime.hour())
            .minute(selectedStartTime.minute())
            .second(0)
            .millisecond(0)
            .format(dateTimeFormat);

        const endDateTime = selectedDate
            .hour(selectedEndTime.hour())
            .minute(selectedEndTime.minute())
            .second(0)
            .millisecond(0)
            .format(dateTimeFormat);

        return {startDateTime, endDateTime};
    }

    const handleDownload = async () => {
        try {
            const datesAreValid = checkDatesValidation();

            if (!datesAreValid) {
                return;
            }

            const {startDateTime, endDateTime} = prepareDatesForApi();

            const response = await downloadIotStateData(batteryID, startDateTime, endDateTime);

            if (!response.data) {
                throw new Error("Failed to fetch file");
            }

            downloadCSVFile(response);
        } catch (err) {
            console.error("Download failed:", err);
        }
    }

    useEffect(() => {
        hideAllErrors();
    }, [open]);

    useEffect(() => {
        checkDatesValidation(false);
    }, [selectedStartTime, selectedEndTime]);

    return (
        <StyledDialog open={open} onClose={handleClose} sx={{ p: 3 }}>
            <DialogContent sx={{pb: 0}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                    <Typography variant="fw600s16c000000" sx={{lineHeight: '20px'}}>Download Device Health Data</Typography>
                    <CloseButton onClick={handleClose} />
                </Box>
                <Box sx={{mb: 2.5}}>
                    <Typography variant="fw400s14c000000" sx={{lineHeight: '24px'}}>Select a date for which you want to download this data</Typography>
                    <Box sx={{mt: 1}}><SingleDatePicker handleOnChange={onDateChange} maxDate={localDate} /></Box>
                </Box>
                <Box sx={{mb: 3}}>
                    <Typography variant="fw400s14c000000" sx={{lineHeight: '24px'}}>Define time period. You can select upto 1 hour.</Typography>
                    <Grid2>
                        <Grid2 container gap={1} mt={1}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="fw400s12c686868" sx={{lineHeight: '24px'}}>Start Time</Typography>
                                <SingleTimePicker handleOnChange={onStartTimeChange} />
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="fw400s12c686868" sx={{lineHeight: '24px'}}>End Time</Typography>
                                <SingleTimePicker handleOnChange={onEndTimeChange} isEndTime={true} />
                            </Box>
                        </Grid2>
                        {showEmptyDateTimeError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>* You must define the date & time period before proceeding</Typography>
                        </Box>}
                        {showHourDifferenceError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>* Time period defined must not be more than one hour</Typography>
                        </Box>}
                        {showInvalidTimeError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>* Start time must be earlier than end time</Typography>
                        </Box>}
                    </Grid2>
                </Box>
            </DialogContent>
            {/* Send Button */}
            <DialogActions sx={{ px: 3, pt: 0, pb: 3, justifyContent: "flex-end" }}>
            <PrimaryFormLoadingActionButton
                onClick={handleDownload}
            > {t('actions.download', 'Download')}
            </PrimaryFormLoadingActionButton>
            </DialogActions>
        </StyledDialog>
    )
}
